import React, { useCallback } from "react";
import { api, statusBar, LIMIT_POINTS, ACTION_POINTS } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Users, Wrench, Activity, AlertTriangle } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";

const Stat = ({ icon: Icon, label, value, tone = "slate" }) => (
  <Card className="p-6 border-slate-200 rounded-lg shadow-sm bg-white">
    <div className="flex items-center justify-between">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
        {label}
      </p>
      <Icon className={`h-4 w-4 text-${tone}-500`} />
    </div>
    <p className="mt-3 font-display text-4xl font-black tracking-tighter text-slate-900">
      {value}
    </p>
  </Card>
);

function TotalsTable({ totals }) {
  return (
    <Card className="border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Employee</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Trigger time</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Exposures</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Points</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Status</th>
            <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4 w-1/3">Towards limit</th>
          </tr>
        </thead>
        <tbody>
          {totals.map((t) => {
            const pct = Math.min(100, (t.points / LIMIT_POINTS) * 100);
            return (
              <tr
                key={t.worker_id}
                className="border-b border-slate-200 last:border-0"
                data-testid={`dashboard-row-${t.worker_id}`}
              >
                <td className="p-4 text-sm font-medium text-slate-900">
                  {t.worker_name}
                </td>
                <td className="p-4 text-sm text-slate-700 font-mono">
                  {t.trigger_minutes.toFixed(1)} min
                </td>
                <td className="p-4 text-sm text-slate-700">{t.exposure_count}</td>
                <td className="p-4 text-sm text-slate-900 font-bold font-mono">
                  {t.points.toFixed(1)}
                </td>
                <td className="p-4">
                  <StatusBadge status={t.status} />
                </td>
                <td className="p-4">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${statusBar(t.status)}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

export default function Dashboard() {
  const fetcher = useCallback(
    () => api.get("/dashboard/today").then((r) => r.data),
    []
  );
  const { data, loading } = useFetch(fetcher);

  if (loading) return <p className="text-slate-500">Loading…</p>;

  const s = data?.summary || {};
  const totals = data?.totals || [];

  return (
    <div className="space-y-8" data-testid="manager-dashboard">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          Today · {data?.date}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mt-1">
          Site Overview
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Live HAVS exposure across the site. UK HSE points — action at{" "}
          {ACTION_POINTS}, limit at {LIMIT_POINTS}.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Stat icon={Users} label="Employees" value={s.workers ?? 0} />
        <Stat icon={Wrench} label="Tools" value={s.tools ?? 0} />
        <Stat icon={Activity} label="Today&apos;s exposures" value={s.today_exposures ?? 0} />
        <Stat icon={AlertTriangle} label={`At action ≥${ACTION_POINTS}`} value={s.at_action_or_above ?? 0} tone="red" />
      </div>

      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-4">
          Today&apos;s exposure by employee
        </h2>

        {totals.length === 0 ? (
          <Card className="p-10 text-center text-slate-500 border-dashed">
            No exposures recorded yet today.
          </Card>
        ) : (
          <TotalsTable totals={totals} />
        )}
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Card className="p-5 status-ok border">
          <p className="text-xs font-bold uppercase tracking-wider">OK</p>
          <p className="text-2xl font-display font-bold mt-1">&lt; 80 pts</p>
        </Card>
        <Card className="p-5 status-watch border">
          <p className="text-xs font-bold uppercase tracking-wider">Watch</p>
          <p className="text-2xl font-display font-bold mt-1">80+ pts</p>
        </Card>
        <Card className="p-5 status-action border">
          <p className="text-xs font-bold uppercase tracking-wider">Action</p>
          <p className="text-2xl font-display font-bold mt-1">100+ pts</p>
        </Card>
        <Card className="p-5 status-stop border">
          <p className="text-xs font-bold uppercase tracking-wider">Stop / Limit</p>
          <p className="text-2xl font-display font-bold mt-1">400+ pts</p>
        </Card>
      </div>
    </div>
  );
}
